import React, { useContext, useEffect } from 'react';

import { useQuery } from '@apollo/client/react';
import { Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { Sentry } from '@opendoor/observability/sentry';
import * as analytics from '@opendoor/shared-fe/analytics';
import { Navigate, useLocation } from 'react-router';

import { ApolloGraphQLErrorLike, getApolloGraphQLErrors } from '../apolloError';
import PartnerSpecificHomeLink from '../components/PartnerSpecificHomeLink';
import { SessionContext } from '../config/context';
import { GetSessionDataDocument } from '../graphql/queries/__generated__/GetSessionData';
import type { GetSessionData } from '../graphql/queries/__generated__/GetSessionData';
import { RedirectToLogin } from '../routes/PrivateRoute';
import { ERROR_PAGE_ROUTE } from '../routes/routes';
import { OpendoorLogoDesktop } from './icons/OpendoorLogoDesktop';
import { LinkButton } from './LinkButton';

export const hasAnyUnauthenticatedError = (
  errors: ReadonlyArray<ApolloGraphQLErrorLike> | undefined,
): boolean => {
  if (!errors) {
    return false;
  }

  for (const gqlError of errors) {
    if (gqlError.extensions?.code === 'UNAUTHENTICATED') {
      return true;
    }
  }
  return false;
};

export interface LayoutProps {
  renderContent?: () => React.ReactNode;
  renderNav?: () => React.ReactNode;
  renderSide?: () => React.ReactNode;
  renderLogo?: () => React.ReactNode;
}

const getAccountInfo = (data: undefined | GetSessionData) => {
  if (!data?.session?.human) {
    return undefined;
  }
  const { human } = data.session;
  return {
    email: human.email,
    name: human.first_name || human.full_name,
  };
};

const parseHumanId = (data: undefined | GetSessionData): string => {
  if (data?.session?.human?.id) {
    return 'Human-' + data.session.human.id;
  }
  return '';
};

const useObservability = (data: GetSessionData | undefined) => {
  const accountInfo = getAccountInfo(data);
  const humanId = parseHumanId(data);

  useEffect(() => {
    if (!accountInfo) {
      return;
    }
    if (humanId) {
      analytics.registerWithUserID(humanId, { email: accountInfo.email });
    } else {
      analytics.register({ email: accountInfo.email });
    }
    const scope = Sentry.getCurrentScope();
    const user = scope.getUser();
    scope.setUser({ ...user, email: accountInfo.email });
    // oxlint-disable-next-line react/exhaustive-deps -- react19-baseline: pre-existing, do not copy. Add the missing dependencies rather than suppressing
  }, [humanId, accountInfo?.email]);

  return accountInfo;
};

const DefaultLogo: React.FC = () => (
  <Row className="shrink-0 items-center">
    <PartnerSpecificHomeLink>
      <OpendoorLogoDesktop />
    </PartnerSpecificHomeLink>
    <Text as="span" className="text-body-md pl-10 text-od-content-secondary">
      Partners
    </Text>
  </Row>
);

const LayoutContent: React.FC<
  LayoutProps & {
    data: GetSessionData | undefined;
    loading: boolean;
    refetch: () => Promise<unknown>;
  }
> = ({ data, loading, refetch, renderContent, renderNav, renderSide, renderLogo }) => {
  const session = useContext(SessionContext);
  const accountInfo = useObservability(data);

  const handleLogout = async () => {
    await session.logOut();
    await refetch();
  };

  return (
    <Col className="min-h-screen">
      <Row className="items-center justify-between border-b border-od-border-secondary bg-od-bg-primary px-4 py-3">
        {renderLogo ? renderLogo() : <DefaultLogo />}
        {!loading && accountInfo && (
          <Row className="items-center gap-4">
            <Text as="span" className="text-body-sm hidden md:flex">
              {accountInfo.email}
            </Text>
            <LinkButton
              variant="secondary"
              className="text-label-md"
              aria-label="Sign out"
              analyticsName="partnership-dashboard-sign-out"
              onClick={handleLogout}
            >
              Sign out
            </LinkButton>
          </Row>
        )}
      </Row>
      <Col className="flex-1 lg:flex-row">
        {renderNav && (
          <Col className="shrink-0 overflow-scroll border-b border-od-border-secondary lg:basis-60 lg:overflow-visible lg:border-r lg:border-b-0">
            {renderNav()}
          </Col>
        )}
        <Col className="w-full max-w-375 flex-1 px-4">{renderContent?.()}</Col>
        {renderSide && <Col className="hidden lg:flex lg:shrink-0 lg:basis-75">{renderSide()}</Col>}
      </Col>
    </Col>
  );
};

export const Layout: React.FC<LayoutProps> = (props) => {
  const { pathname } = useLocation();
  const { data, error, loading, refetch } = useQuery(GetSessionDataDocument);

  if (error && pathname !== ERROR_PAGE_ROUTE) {
    if (hasAnyUnauthenticatedError(getApolloGraphQLErrors(error))) {
      return <RedirectToLogin />;
    }
    return <Navigate to={ERROR_PAGE_ROUTE} replace />;
  }
  return (
    <LayoutContent
      {...props}
      data={data as GetSessionData | undefined}
      loading={loading}
      refetch={refetch}
    />
  );
};
